import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { fetchAdminActivities } from "../../redux/slices/adminSlice";
import { SPACING } from "../../theme/constants";

const AdminActivities = () => {
  const dispatch = useDispatch();
  const { activities, activitiesPagination, loading } = useSelector(
    (state) => state.admin,
  );
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    action: "",
  });

  useEffect(() => {
    dispatch(fetchAdminActivities(filters));
  }, [dispatch, filters]);

  const getActionColor = (action) => {
    if (action?.includes("DELETE")) return "error";
    if (action?.includes("CREATE") || action?.includes("CREATED"))
      return "success";
    if (action?.includes("UPDATE") || action?.includes("UPDATED"))
      return "info";
    return "default";
  };

  return (
    <Box>
      <Box sx={{ mb: SPACING.lg }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
          System Activities
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All user activities across the platform
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={filters.action}
              label="Action Type"
              onChange={(e) =>
                setFilters({ ...filters, action: e.target.value, page: 1 })
              }
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="SITE_CREATED">Site Created</MenuItem>
              <MenuItem value="SITE_DELETED">Site Deleted</MenuItem>
              <MenuItem value="USER_CREATED">User Created</MenuItem>
              <MenuItem value="USER_UPDATED">User Updated</MenuItem>
              <MenuItem value="USER_DELETED">User Deleted</MenuItem>
              <MenuItem value="BACKUP_CREATED">Backup Created</MenuItem>
              <MenuItem value="SYSTEM_SETTINGS_UPDATED">
                Settings Updated
              </MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Activities Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activities.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No activities found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    activities.map((activity) => (
                      <TableRow key={activity.id} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: "0.75rem",
                              }}
                            >
                              {activity.user?.firstName?.[0] || "?"}
                            </Avatar>
                            <Typography variant="body2">
                              {activity.user
                                ? `${activity.user.firstName || ""} ${activity.user.lastName || ""}`.trim() ||
                                  activity.user.email
                                : "System"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={(
                              activity.activityType || activity.action
                            )?.replace(/_/g, " ")}
                            size="small"
                            color={getActionColor(
                              activity.activityType || activity.action,
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {activity.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(activity.createdAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {activitiesPagination && (
              <TablePagination
                component="div"
                count={activitiesPagination.total || 0}
                page={(activitiesPagination.page || 1) - 1}
                onPageChange={(_, newPage) =>
                  setFilters({ ...filters, page: newPage + 1 })
                }
                rowsPerPage={filters.limit}
                onRowsPerPageChange={(e) =>
                  setFilters({
                    ...filters,
                    limit: parseInt(e.target.value, 10),
                    page: 1,
                  })
                }
                rowsPerPageOptions={[10, 20, 50]}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AdminActivities;
